import _ from 'lodash'
import Backbone from 'backbone'
import { isV5, isV6 } from 'scoreboard/components/version'

var constants = {
	// the number of milliseconds after which we'll show loading dots when a
	// screen is taking some time to load
	LOADING_TIME: 150,
	// different modes for the application
	MODE_INSTALL: 'install',
	MODE_CONFIGURE: 'configure',
	MODE_OPERATIONAL: 'operational',

	// Layouts the app can take
	LAYOUT_EXPORT: 'layout-export',
	LAYOUT_FULLSCREEN: 'layout-fullscreen',
	// TODO: Remove when V5 is no longer supported (dashboard edit fullscreen)
	LAYOUT_DASHBOARD_EDIT_FULLSCREEN: 'layout-dashboard-edit-fullscreen',
	LAYOUT_DASHBOARD_SHARED: 'layout-dashboard-shared',
	LAYOUT_FORM_SHARED: 'layout-form-shared',
	LAYOUT_STANDARD: 'layout-standard',

	// Briefings related view modes
	BRIEFING_MODE_PRESENTATION: 'briefing-mode-presentation',
	BRIEFING_MODE_IS_PAUSED: 'briefing-mode-is-paused',
	BRIEFING_MODE_IS_EDITING_SLIDE: 'briefing-mode-is-editing-slide',
	BRIEFING_MODE_IS_EDITING_BRIEFING: 'briefing-mode-is-editing-briefing',
	BRIEFING_MODE_IS_VIEWING_SLIDE: null,

	/** from {@link AppConfigVariable.java} */
	APP_CERTIFICATE_PEM: 'appCertificatePem',
	APP_CERTIFICATE_PRIVATE_KEY_PEM: 'appCertificatePrivateKeyPem',

	// scoring types
	TWO_COLOR: 'two-color',
	GOAL_RED_FLAG: 'goal-red-flag',
	THREE_COLOR: 'three-color',
	FOUR_COLOR_ORANGE: 'four-color-orange',
	FOUR_COLOR_BLUE: 'four-color-blue',
	STABILIZE_2: 'stabilize-2',
	STABILIZE_3: 'stabilize-3',
	YES_NO: 'yes-no',
	GOAL_ONLY: 'goal-only',
	UNSCORED: 'unscored',
	SCORED_MIDDLE_4_COLOR: 'scored-middle-4-color',

	// kpi value types
	NUMBER: 'number',
	STABILIZE_NUMBER: 'stabilize-number',
	SCORED_TEXT: 'scored-text',
	UNSCORED_TEXT: 'unscored-text',

	// scorecard item types
	ROOT: 'root',
	GENERIC: 'generic',
	PERSPECTIVE: 'perspective',
	OBJECTIVE: 'objective',
	METRIC: 'metric',
	LINKED: 'linked',
	DOCKING: 'docking',

	// aggregation types
	SUM: 'sum',
	AVERAGE: 'average',
	LAST_VALUE: 'last-value',
	PERCENTAGE_YES: 'percentage-yes',
	NUMBER_OF_YESES: 'number-of-yeses',
	USE_EQUATION: 'use-equation',
	LIST_UNIQUE_VALUES: 'list-unique-values',
	GEOMETRIC_MEAN: 'geometric-mean',
	PERCENTAGE: 'percentage',
	MINIMUM: 'minimum',
	MAXIMUM: 'maximum',
	MIN_DATE: 'min-date',
	MAX_DATE: 'max-date',
	MIN_TIME: 'min-time',
	MAX_TIME: 'max-time',
	NONE: 'none',
	UNIQUE: 'unique',
	COUNT: 'count',
	PERCENTAGE_REACHING_GOAL: 'percentage-reaching-goal',
	NUMBER_REACHING_GOAL: 'number-reaching-goal',

	ORIENTATION_43: '4:3',
	ORIENTATION_LANDSCAPE: 'landscape',
	ORIENTATION_PORTRAIT: 'portrait',

	// device size definitions
	// below this width we should treat the device as a phone (some
	// small screen device)
	SIZE_PHONE_WIDTH: 700,
	// below this width (but above SIZE_PHONE_WIDTH) we should treat the devices as a phablet
	// or some phone which has a big screen.
	// above this size the device should be treated as a tablet
	SIZE_PHABLET_WIDTH: 850,
	// When the width or height of the browser is less than or equal to one of
	// the mobile breakpoints, we collapse to mobile navigation.
	MOBILE_WIDTH_BREAKPOINT: 800,
	MOBILE_HEIGHT_BREAKPOINT: 550,
	MOBILE_MENU_WIDTH: 350,
	HEAD_PANE_MIN_WIDTH: 126,
	HEAD_PANE_COLLAPSED_WIDTH: 44,
	ORG_PANE_TRANSITION_MARGIN: 27,
	PROJECT_MENU_MAX_WIDTH: 275,
	PROJECT_MENU_COLLAPSED_WIDTH: 60,
	// Matches $nav-pane-min-width in nav-pane.scss
	NAV_PANE_MIN_WIDTH: 140,
	// the height in px of the min-height-vertical-center container
	MIN_HEIGHT_VERTICAL_CENTER_HEIGHT: 75,
	// the breakpoint between showing the edit menu in a tooltip or a dialog
	// should match the value in org-pane.scss for the media query for
	// .org-pane.is-editing
	EDIT_MENU_CUTOFF: 600,

	// Standard margin of 20px to match CSS variable
	STANDARD_MARGIN: 20,

	// Standard border width. Matched $border-width in measurements.scss
	BORDER_WIDTH: 1,

	// Match CSS variables in line-and-bands.css
	CHART_DESIGNER_PANEL_PADDING: 8,
	LINES_AND_BANDS_HEIGHT: 34,

	// Match CSS transiiton variable $animation-chart-designer-panel-reveal
	CHART_PANEL_TRANSITION_TIME: 300,

	// Size of the logged-in-as-banner
	LOGGED_IN_AS_BANNER_HEIGHT: 50,

	// Display size classes the body can have
	DISPLAY_SIZES: ['display-small', 'display-medium', 'display-large', 'display-x-large'],

	// Bootslide menu values
	DEFAULT_BOOTSLIDE_MENU_WIDTH: 160,

	MIN_DISPLAY_PANE_WIDTH: isV6() ? 340 : 220,

	// Color picker menu values
	BOOTSLIDE_MENU_COLOR_PICKER_WIDTH: `100%`,

	// limit of items to show when previewing results from the report designers
	REPORT_PREVIEW_LIMIT: 4,
	// The breakpoint between showing the org/project pane edit menu in a tooltip or a dialog.
	// This should match the value in layout.scss for the media query for
	// .org-pane.is-editing, .project-pane.is-editing
	NAV_EDIT_MENU_CUTOFF: 600,

	// .5 sec delay used before expanding a tree to show the full name of a node
	TREE_EXPAND_DELAY: 500,
	// 330 equals .33s contract transition on projectOrganizationMenu set in the SCSS
	TREE_CONTRACT_DELAY: 330,
	// The tree node mask width is the approximate width of the label mask
	// gradient which covers the text in order to fade it out.
	TREE_NODE_MASK_WIDTH: 31,

	// 200ms delay after user stops typing before searches are performed
	SEARCH_DELAY: 200,

	// The room on the right side of the expandable pane used to click to exit
	// the expanded state
	EXPAND_COLLAPSE_EXIT_SPACE: 50,

	FLOATING_PANEL_MIN_HEIGHT: 435,

	// The height of the mobile header displayed in the admin/home sections
	MOBILE_HEADER_HEIGHT: 53,

	// A prefix used to indicated that an id is unsaved, for use on unpersisted
	// objects which required an id to be set so that UI logic works on both
	// persisted and unpersisted objects
	UNSAVED_PREFIX: 'unsaved_',

	// Amount of time the bootslide menu stays faded out to show the selection made.
	// 600 gives the animation time to happen before the bootslide menu is shown again.
	PREVIEW_BOOTSLIDE_SELECTION_DELAY: 600,

	// Amount matches SASS variable $equation-editor-min-width
	EQUATION_EDITOR_MIN_WIDTH: 800,

	// Height of our default inputs (measurements.scss:$input-height)
	INPUT_HEIGHT: 34,

	// Drawer Layout (values mirror vars in measurements.scss)
	// The height of the drawer when it is closed and ready to be opened
	DRAWER_CLOSED_HEIGHT: 18,
	// Height of the drawer when it is always open and no open/close toggling is needed
	DRAWER_ALWAYS_OPEN_HEIGHT: isV6() ? 163 : 193,

	// Dashboards
	DASHBOARD_MOBILE_TOOLBAR_HEIGHT: 50,

	// The time in ms to distinguish between a click vs a drag. If the mouse is
	// held down longer than this threshold we start a drag. This number is the
	// same one used by the kalpa tree
	// https://github.com/SpiderStrategies/Scoreboard/issues/13135#issuecomment-326809075
	DRAG_THRESHOLD_MS: 250,

	// Tooltip Values
	TOOLTIP_ARROW_HEIGHT: 9,
	TOOLTIP_ARROW_WIDTH: 9,
	TOOLTIP_ARROW_SPACING_FROM_EDGE: 20,
	// sync with bootslide.scss
	BOOTSLIDE_TILE_WIDTH: 140,
	BOOTSLIDE_TILE_MARGIN: 15,

	// emdash surrounded by non-breaking spaces
	SPACED_EMDASH: '\u00A0\u2014\u00A0',

	// Object selector default height, must be in sync with measurements.scss:object-selector-height
	OBJECT_SELECTOR_HEIGHT: 400,

	// TimeDisplayType.kt
	HOUR_12: 'hour-12',
	HOUR_24: 'hour-24',
	HOUR_12_SECONDS: 'hour-12-seconds',
	HOUR_24_SECONDS: 'hour-24-seconds',

	// ImportConnectionType.kt
	DATABASE: 'database',
	FTP: 'ftp',
	GOOGLE_SHEETS: 'google-sheets',
	ZAPIER: 'zapier',
	WEB_SERVICE: 'web-service',

	// WsType.kt
	REST: 'rest',
	SOAP: 'soap',

	// HttpMethod.kt
	GET: 'get',
	POST: 'post',

	// DatasetFieldType.kt
	DATASET_FIELD_TYPE: {
		CALCULATED: 'calculated',
		BASIC: 'basic',
		CLUSTER: 'cluster',
		MANUAL: 'manual',
	},

	// ImportDataSourceType.java
	IMPORT_DATA_SOURCE_TYPE: {
		DATABASE: 'database',
		UPLOAD: 'upload',
		DOCUMENT: 'document',
		FTP: 'ftp',
		GOOGLE: 'google',
		ZAPIER: 'zapier',
		REST: 'rest'
	},

	// FormActionEditor.js
	SET_FIELD_VALUE_TYPE: {
		SPECIFIC_VALUE: 'specific-value',
		BLANK: 'blank',
		CURRENT_DATE_TIME: 'current-date-time',
		USER_EMAIL: 'user-email',
		USER_NAME: 'user-name',
		USER_USERNAME: 'user-username',
		USER_ID: 'user-id',
		USER_IP_ADDRESS: 'user-ip-address',
	},

	// Matches SCSS var in colored-cells.scss
	COLORED_CELL_BG_OPACITY: 1,

	// Issue #56343: The lowest value that allows for our standard score colors
	// of Yellow, Red and Green to not not get a text-shadow applied to them.
	TEXT_VARIATION_THRESHOLD: .91,
	// Issue #56692: If text is against a colored background, we'll use a
	// different threshold
	COLORED_BACKGROUND_TEXT_VARIATION_THRESHOLD: .8,

	HTTP_HEADERS: {
		CONTENT_TYPE: 'Content-Type',
	},

	CONTENT_TYPES: {
		APPLICATION_X_WWW_FORM_URLENCODED: 'application/x-www-form-urlencoded',
		APPLICATION_JSON: 'application/json;charset=UTF-8',
	},

	// Prefix used to specify rich-text-color css classes
	RICH_TEXT_COLOR_PREFIX: 'rich-text-color-',

	// Sections of the app that ativate the library option in the Top Nav
	LIBRARY_SECTIONS: [
		'briefings',
		'strategyMaps',
		'dashboards',
		'reports',
		'charts',
		'scorecards',
		'initiatives',
		'datasets',
		'forms',
		'files'
	],
}

// we have to do this because constants are injected into places they're
// required before they've actually been loaded
Backbone.on('load', function(data) {
	if (data.constants) {
		_.extend(constants, data.constants)
	}

})

export default constants

constants.__filename__ = 'components/constants.js'
